import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SyncTriggerType = 'bills' | 'products' | 'profiles' | 'all';

export interface EntityCounts {
    unsynced: number;
    synced: number;
    totalProcessed: number;
}

export interface SyncDashboardStatus {
    checkedAt: string;
    institutionId: string;
    healthy: boolean;
    failedBatches: number;
    lastSuccessfulRun: string | null;
    lastFailedRun: string | null;
    lastError: string | null;
    bills: EntityCounts;
    products: EntityCounts;
    profiles: EntityCounts;
}

export interface ConnectivityResult {
    checkedAt: string;
    gatewayUrl: string;
    institutionId: string;
    reachable: boolean;
    responseTimeMs: number;
    error?: string;
}

export interface TriggerResult {
    status: 'OK' | 'ERROR';
    message: string;
    profilesGenerated?: number;
}

// ── Queries ───────────────────────────────────────────────────────────────────

/** Main polling hook — combines loader health metrics + entity pending counts */
export const useSyncStatus = () =>
    useQuery<SyncDashboardStatus>({
        queryKey: ['sync-status'],
        queryFn: () => axios.get('/api/payment-sync/status').then((r) => r.data),
        refetchInterval: 15_000,
    });

/** Loader health only — lighter endpoint from existing LoaderHealthController */
export const useLoaderHealth = () =>
    useQuery<{ status: string }>({
        queryKey: ['loader-health'],
        queryFn: () => axios.get('/api/loader-health/health').then((r) => ({ status: r.data })),
        refetchInterval: 30_000,
    });

// ── Mutations ─────────────────────────────────────────────────────────────────

export const useTriggerSync = () => {
    const qc = useQueryClient();
    return useMutation<TriggerResult, Error, SyncTriggerType>({
        mutationFn: (type) =>
            axios.post(`/api/payment-sync/trigger/${type}`).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['sync-status'] }),
    });
};

export const useTestConnectivity = () =>
    useMutation<ConnectivityResult, Error>({
        mutationFn: () => axios.get('/api/payment-sync/connectivity').then((r) => r.data),
    });

export const useResetMetrics = () => {
    const qc = useQueryClient();
    return useMutation<void, Error>({
        mutationFn: () => axios.post('/api/payment-sync/reset-metrics').then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['sync-status'] }),
    });
};
